import { v4 as uuid } from 'uuid';

import { calculateWeight, sortByWeight } from '@techchange/dnd';

import {
	CONTINUATION_TYPES,
	FIELD_TYPES,
	FORM_TYPES,
} from '../../../../../constants';
import { findFormFieldIndices } from '../../../../../utils';
import {
	FORM_EDITOR_FIELDS,
	FIELD_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from '../constants';

export const FORM_FIELD_OPTION_DATA_DISPATCH = {
	ADD_FORM_FIELD_OPTION: 'ADD_FORM_FIELD_OPTION',
	DELETE_FORM_FIELD_OPTION: 'DELETE_FORM_FIELD_OPTION',
	MOVE_FORM_FIELD_OPTION: 'MOVE_FORM_FIELD_OPTION',
	UPDATE_FORM_FIELD_OPTION: 'UPDATE_FORM_FIELD_OPTION',
	UPDATE_FORM_FIELD_OPTION_CONTENTS: 'UPDATE_FORM_FIELD_OPTION_CONTENTS',
};

/**
 * Set of functions that update the form field options state for a given action type.
 */
export const formFieldOptionDataReducers = {
	[FORM_FIELD_OPTION_DATA_DISPATCH.ADD_FORM_FIELD_OPTION]:
		addFormFieldOption,
	[FORM_FIELD_OPTION_DATA_DISPATCH.DELETE_FORM_FIELD_OPTION]:
		deleteFormFieldOption,
	[FORM_FIELD_OPTION_DATA_DISPATCH.MOVE_FORM_FIELD_OPTION]:
		moveFormFieldOption,
	[FORM_FIELD_OPTION_DATA_DISPATCH.UPDATE_FORM_FIELD_OPTION]:
		updateFormFieldOption,
	[FORM_FIELD_OPTION_DATA_DISPATCH.UPDATE_FORM_FIELD_OPTION_CONTENTS]:
		updateFormFieldOptionContents,
};

/**
 * Adds a new option to a form field in the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action          Data related to the requested action.
 * @param {String} action.fieldID  The ID of the form field in which to add the option.
 * @param {String} action.defaultLanguage  The default language for the form.
 *
 * @returns {Object} result  The updated form state.
 */
export function addFormFieldOption(formData, action) {

	const { fieldID, defaultLanguage } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	const formType = result.type;

	// Find the field from the nested state
	const [sectionIdx, fieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalFieldOptions =
		originalSectionFields[fieldIdx][FIELD_EDITOR_FIELDS.OPTIONS];

	// Find the maximum weight for the existing options
	const prev = Math.max(
		...originalFieldOptions.map((option) => option[OPTION_EDITOR_FIELDS.WEIGHT]),
		0,
	);

	const newOption = {
		[OPTION_EDITOR_FIELDS.ID]: uuid(),
		[OPTION_EDITOR_FIELDS.VALUE]: null,
		[OPTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
		[OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
		[OPTION_EDITOR_FIELDS.WEIGHT]: calculateWeight(prev, null),
		[OPTION_EDITOR_FIELDS.FORM_FIELD_ID]: fieldID,
		[OPTION_EDITOR_FIELDS.CONTENTS]: [{
			language: defaultLanguage,
			[OPTION_EDITOR_FIELDS.LABEL]: null,
		}],
		[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
	};

	if (formType === FORM_TYPES.QUIZ) {
		newOption[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] = false;
	}

	// Create the new option for the field placing it at the end
	originalFieldOptions.push(newOption);

	return result;

}

/**
 * Updates an option for a form field.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.fieldID     The field to update.
 * @param {String} action.optionID    The option to update.
 * @param {String} action.fieldName   The name of the field to update for the option.
 * @param {String} action.fieldValue  The new value of the field to update for the option.
 *
 * @returns {Object} result  The updated form state.
 */
export function updateFormFieldOption(formData, action) {

	const { fieldID, optionID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [sectionIdx, fieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalField = originalSectionFields[fieldIdx];

	const originalFieldOptions = originalField[FIELD_EDITOR_FIELDS.OPTIONS];

	const optionIndex = originalFieldOptions.findIndex((option) => option.id === optionID);

	// Update the field data
	originalFieldOptions[optionIndex] = {
		...originalFieldOptions[optionIndex],
		[fieldName]: fieldValue,
	};

	// Check if the update included choosing an option as the correct answer
	if (fieldName === OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER && fieldValue === true) {

		// Check if the field only allows one correct answer
		if (originalField[FIELD_EDITOR_FIELDS.TYPE] === FIELD_TYPES.RADIO) {

			// Loop through the other options and switch to incorrect answers
			for (let i = 0, len = originalFieldOptions.length; i < len; i++) {

				if (i !== optionIndex) {

					originalFieldOptions[i] = {
						...originalFieldOptions[i],
						[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER]: false,
					};

				}

			}

		}

	}

	return result;

}

/**
 * Determines the updated state to reflect the field value for an
 * attribute of a form field option content object.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.fieldID     The field to update.
 * @param {String} action.optionID    The option to update.
 * @param {String} action.fieldName   The name of the field to update for the option.
 * @param {String} action.fieldValue  The new value of the field to update for the option.
 *
 * @returns {Object}  The updated form state.
 */
function updateFormFieldOptionContents(formData, action) {

	const { fieldID, optionID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	const selectedLanguage = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Find the field from the nested state
	const [sectionIdx, fieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalField = originalSectionFields[fieldIdx];

	const originalFieldOptions = originalField[FIELD_EDITOR_FIELDS.OPTIONS];

	const optionIndex = originalFieldOptions.findIndex((option) => option.id === optionID);

	const originalOptionData = originalFieldOptions[optionIndex];

	// Get the existing multi-language content for the option
	const updatedOptionContents = originalOptionData[OPTION_EDITOR_FIELDS.CONTENTS];

	// Find the translated content for the current language, if any
	const activeTranslationIdx = updatedOptionContents
		.findIndex((content) => content.language === selectedLanguage);

	// Update the multi-language content for the option
	if (activeTranslationIdx >= 0) {

		updatedOptionContents[activeTranslationIdx] = {
			...updatedOptionContents[activeTranslationIdx],
			[fieldName]: fieldValue,
		};

	}

	return result;

}

/**
 * Moves an option for a form field.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action           Data related to the requested action.
 * @param {String} action.fieldID   The field to update.
 * @param {String} action.optionID  The option to move.
 * @param {Object} action.prevItem  The previous option.
 * @param {Object} action.nextItem  The next option.
 *
 * @returns {Object} result  The updated form state.
 */
export function moveFormFieldOption(formData, action) {

	const { fieldID, optionID, prevItem, nextItem } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [sectionIdx, fieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalField = originalSectionFields[fieldIdx];

	const originalFieldOptions = originalField[FIELD_EDITOR_FIELDS.OPTIONS];

	// Find the index of the "moved" option in the list of options
	const optionIndex = originalFieldOptions.findIndex((option) => option.id === optionID);

	// Check that at least either the previous or next option is valid
	if (!prevItem && !nextItem) {
		return result;
	}

	const prev = prevItem?.[OPTION_EDITOR_FIELDS.WEIGHT];
	const next = nextItem?.[OPTION_EDITOR_FIELDS.WEIGHT];

	// Calculate the new weight of the "moved" option;
	const weight = calculateWeight(prev, next);

	originalFieldOptions[optionIndex] = {
		...originalFieldOptions[optionIndex],
		[OPTION_EDITOR_FIELDS.WEIGHT]: weight,
	};

	// Re-sort the list of options
	const sortedOptions = sortByWeight(originalFieldOptions);

	// eslint-disable-next-line max-len
	result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS][fieldIdx][FIELD_EDITOR_FIELDS.OPTIONS] = sortedOptions;

	return result;

}

/**
 * Deletes an option from a form field.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action           Data related to the requested action.
 * @param {String} action.fieldID   The field to update.
 * @param {String} action.optionID  The option to delete.
 *
 * @returns {Object} result  The updated form state.
 */
export function deleteFormFieldOption(formData, action) {

	const { fieldID, optionID } = action;

	// Create a (deep) copy of the field data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [sectionIdx, fieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalField = originalSectionFields[fieldIdx];

	const originalFieldOptions = originalField[FIELD_EDITOR_FIELDS.OPTIONS];

	// Find the index of the "deleted" option in the list of options
	const optionIndex = originalFieldOptions.findIndex((option) => option.id === optionID);

	originalFieldOptions.splice(optionIndex, 1);

	return result;

}
